import {
  Banner,
  Button,
  Divider,
  InputSelect,
  InputWrapper,
} from '@vrplatform/components';
import {
  type QueryGroup,
  type QueryKeyUnion,
  captureSentryError,
  useApiClient,
  useApiIsTeamIdFetching,
  useApiIsTeamSwitching,
  useInvalidateApiQueries,
  useInvalidateHasuraQueries,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import type { RouterInput } from '@vrplatform/trpc-api';
import type { SelectItem } from '@vrplatform/components';
import {
  showErrorNotification,
  showSuccessNotification,
  showWarnNotification,
  useAppName,
} from '@vrplatform/theme';
import { toTitleCase } from '@vrplatform/utils';
import {
  Anchor,
  Avatar,
  Box,
  Card,
  Collapse,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { Dropzone, type FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { useDisclosure } from '@mantine/hooks';
import { parse } from 'papaparse';
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useAutomationHistoryDrawer } from '../../hooks';
import { getCsvUrl } from '../../utils';
import {
  getCsvImportTeamScopeBlockReason,
  readCsvImportFile,
} from './CsvImportModal.utils';
import { PublicGoogleSheetsParser } from './_google-sheet-parser';
import type { CsvRowError } from './_types';

type FormInputs = {
  file: FileWithPath;
  dateFormat: string | undefined;
};

type ImportUniqueRef = RouterInput['importCsv']['input']['uniqueRef'];

type BaseRow = Record<string, string | null | boolean | number>;

const isFileTypeError = (message: string) => {
  return message?.toLowerCase() === 'file type must be text/csv';
};

const isFileReadError = (message: string) => {
  const lower = message?.toLowerCase() || '';
  return (
    lower.includes('failed to read csv file') ||
    lower.includes('csv file read was aborted')
  );
};

type Props<TRow extends BaseRow> = {
  template?: TRow[] | string;
  opened: boolean;
  closeModal: () => void;
  successMessage: {
    title: string;
    message: string;
  };
  modal: {
    title: string;
    appIcon?: string;
    documentationUrl?: string;
    uniqueRef: ImportUniqueRef;
    googleSheetId?: string;
    showDateFormatInput: boolean;
    description?: ReactNode;
    submitLabel?: string;
    templateLinkLabel?: string;
  };
  invalidateQueryKeys: (QueryKeyUnion | (string & {}))[];
  invalidateApiQueryGroups?: QueryGroup[];
  accountId?: string;
  connectionId: string | undefined;
  onSuccess?: (data: TRow[] | null) => void;
};

export const BaseCsvImportModal = <TRow extends BaseRow>({
  template,
  opened,
  closeModal,
  successMessage,
  modal,
  invalidateQueryKeys,
  invalidateApiQueryGroups,
  accountId,
  connectionId,
  onSuccess,
}: Props<TRow>) => {
  // const { mutateAsync } = useApiMutation('post', '/csv/import');
  const $api = useApiClient();

  const invalidateHasura = useInvalidateHasuraQueries(invalidateQueryKeys);
  const invalidateApi = useInvalidateApiQueries(invalidateApiQueryGroups);

  const { open: openAutomationHistoryDrawer } = useAutomationHistoryDrawer();

  const handleResult = useCallback<
    ComponentProps<typeof ModalBase>['handleResult']
  >(
    async (fileData, fileName, dateFormat) => {
      if (!connectionId) {
        const title = 'Failed to find connection ID for this import.';
        captureSentryError(title);
        showWarnNotification({
          title,
          message: 'Please contact support if this issue persists.',
        });

        return {
          rowErrors: [],
        };
      }

      const result = await $api.POST('/csv/import', {
        body: {
          fileData,
          fileName,
          connectionId,
          uniqueRef: modal.uniqueRef,
          params: { accountId, dateFormat },
        },
      });

      // status code !== 200
      if (result.error) {
        return {
          fileError:
            result.error.issues?.[0]?.message ||
            `[${result.error.code}] ${result.error.message}`,
        };
      }

      const data = result.data.data;

      if (data.status === 'completed') {
        if (onSuccess) {
          const rows = parse<TRow>(fileData, {
            skipEmptyLines: true,
            header: true,
          });
          onSuccess(rows.errors.length ? null : rows.data);
        } else {
          if (data.syncId) {
            openAutomationHistoryDrawer(data.syncId);
          }
        }

        invalidateHasura();
        invalidateApi();
        showSuccessNotification({
          title: successMessage.title,
          message: successMessage.message,
        });

        return {};
      }

      if (data.parseErrors?.length) {
        return {
          rowErrors: data.parseErrors,
        };
      }

      return {
        fileError: data.error,
      };
    },
    [
      successMessage.title,
      successMessage.message,
      modal.uniqueRef,
      accountId,
      connectionId,
      onSuccess,
      invalidateApi,
      invalidateHasura,
      $api.POST,
      openAutomationHistoryDrawer,
    ]
  );

  return (
    <ModalBase
      closeModal={closeModal}
      opened={opened}
      title={modal.title}
      type={modal.uniqueRef}
      appIcon={modal.appIcon}
      handleResult={handleResult}
      template={template}
      documentationUrl={modal.documentationUrl}
      showDateFormatInput={modal.showDateFormatInput}
      googleSheetId={modal.googleSheetId}
      description={modal.description}
      submitLabel={modal.submitLabel}
      templateLinkLabel={modal.templateLinkLabel}
    />
  );
};

const ModalBase = <TRow extends BaseRow>({
  closeModal: cl,
  opened,
  title,
  type,
  template,
  googleSheetId,
  handleResult,
  documentationUrl,
  showDateFormatInput,
  appIcon,
  description,
  submitLabel,
  templateLinkLabel,
}: {
  opened: boolean;
  closeModal: () => void;
  title: string;
  type: ImportUniqueRef;
  handleResult: (
    fileData: string,
    fileName: string,
    dateFormat: FormInputs['dateFormat']
  ) => Promise<{ rowErrors?: CsvRowError[]; fileError?: string }>;
  template: TRow[] | string | undefined;
  showDateFormatInput: boolean;
  googleSheetId: string | undefined;
  documentationUrl?: string;
  appIcon?: string;
  description?: ReactNode;
  submitLabel?: string;
  templateLinkLabel?: string;
}) => {
  const [errors, setErrors] = useState<
    | {
        rowErrors: CsvRowError[];
        fileError?: string;
      }
    | undefined
  >(undefined);
  const { appName } = useAppName();
  const isTeamIdFetching = useApiIsTeamIdFetching();
  const isTeamSwitching = useApiIsTeamSwitching();
  const teamScopeBlockReason = getCsvImportTeamScopeBlockReason({
    isTeamIdFetching,
    isTeamSwitching,
  });

  const theme = useMantineTheme();
  const methods = useForm<FormInputs>({
    mode: 'onChange',
  });
  const selectedFile = methods.watch('file');
  const selectedDateFormat = methods.watch('dateFormat');
  const canSubmit =
    !!selectedFile &&
    (!showDateFormatInput || !!selectedDateFormat) &&
    !teamScopeBlockReason;

  const templateHref = useMemo(() => {
    if (typeof template === 'string') {
      return template;
    }

    if (!template) return undefined;

    return getCsvUrl(template);
  }, [template]);

  const closeModal = useCallback(() => {
    methods.reset({
      file: undefined,
    });
    methods.clearErrors();
    setErrors(undefined);
    cl();
  }, [methods, cl]);

  const submit = useCallback(
    async (data: FormInputs) => {
      if (teamScopeBlockReason) {
        showWarnNotification({
          title: 'Team access is updating',
          message: teamScopeBlockReason,
        });
        return;
      }

      try {
        const fileData = await readCsvImportFile(data.file);

        if (typeof fileData !== 'string') {
          throw new Error('Failed to parse CSV to string');
        }

        const res = await handleResult(
          fileData,
          data.file.name,
          data.dateFormat
        );

        if (res.rowErrors?.length || res.fileError) {
          setErrors({
            rowErrors: res.rowErrors ?? [],
            fileError: res.fileError,
          });

          return;
        }

        closeModal();
      } catch (error: any) {
        const message = error?.message || 'Failed to parse CSV file.';

        if (!isFileTypeError(message) && !isFileReadError(message))
          captureSentryError(error);

        showWarnNotification({
          title: 'Invalid file',
          message,
        });
      }
    },
    [closeModal, handleResult, teamScopeBlockReason]
  );

  useEffect(() => {
    if (!opened)
      methods.reset({
        file: undefined,
      });
  }, [opened, methods.reset]);

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title={
        appIcon ? (
          <Group wrap="nowrap" gap="sm">
            <Avatar
              src={appIcon}
              size="sm"
              sx={(theme) => ({
                border: `1px solid ${theme.colors.gray[2]}`,
              })}
            />
            <Text fw={500} size="md">
              {title}
            </Text>
          </Group>
        ) : (
          title
        )
      }
      size={500}
      centered
      styles={{
        title: {
          fontWeight: 500,
          fontSize: theme.fontSizes.lg,
        },
        content: {
          padding: theme.spacing.sm,
        },
      }}
    >
      <Text component="p" c="neutral" mb="xs">
        {description ?? (
          <>
            Upload a CSV file to add {type} to {appName}. Your CSV should
            include header and follow specified structure.
          </>
        )}
      </Text>

      <Text component="p" c="neutral" mb="md">
        {googleSheetId ? (
          <Anchor
            component="button"
            onClick={(event) => {
              event.preventDefault();

              try {
                const parser = new PublicGoogleSheetsParser(googleSheetId, {
                  useFormat: true,
                });
                parser.parse().then((data: any) => {
                  const url = getCsvUrl(data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${type}_import_template.csv`;
                  a.click();
                });
              } catch (error: any) {
                captureSentryError(error);
                showWarnNotification({
                  title: 'Failed to download template',
                  message: 'Please contact support if this issue persists.',
                });
              }
            }}
          >
            {templateLinkLabel ?? 'Download CSV Template'}
          </Anchor>
        ) : (
          !!templateHref && (
            <Anchor
              href={templateHref}
              download={`${type}_import_template.csv`}
            >
              {templateLinkLabel ?? 'Download CSV Template'}
            </Anchor>
          )
        )}
        {documentationUrl && (
          <>
            {' '}
            or read our{' '}
            <Anchor
              href={documentationUrl}
              target="_blank"
              rel="noopener,noreferrer"
            >
              documentation
            </Anchor>
            .
          </>
        )}
      </Text>

      <Box
        component="form"
        onSubmit={methods.handleSubmit(submit)}
        onReset={closeModal}
      >
        {showDateFormatInput && (
          <>
            <Controller
              control={methods.control}
              name="dateFormat"
              rules={{
                required: showDateFormatInput
                  ? 'Please select a date format.'
                  : false,
              }}
              defaultValue={'YYYY-MM-DD'}
              render={({ field, fieldState }) => {
                const options: SelectItem[] = [
                  {
                    label: 'YYYY-MM-DD',
                    value: 'YYYY-MM-DD',
                  },
                  {
                    label: 'MM/DD/YYYY',
                    value: 'MM/DD/YYYY',
                  },
                  {
                    label: 'DD/MM/YYYY',
                    value: 'DD/MM/YYYY',
                  },
                  {
                    label: 'DD.MM.YYYY',
                    value: 'DD.MM.YYYY',
                  },
                ];

                const value =
                  options.find((o) => o.value === field.value) ?? null;

                return (
                  <InputWrapper
                    label={'Date Format'}
                    error={fieldState.error?.message}
                  >
                    <InputSelect
                      type="single"
                      data={{
                        options,
                      }}
                      value={value}
                      inputProps={{
                        error: !!fieldState.error,
                      }}
                      setValue={(v) => {
                        if (v?.value) field.onChange(v.value);
                      }}
                    />
                  </InputWrapper>
                );
              }}
            />

            <Divider mt="lg" mb="md" />
          </>
        )}

        <Controller
          control={methods.control}
          name="file"
          rules={{
            required: 'Please upload a file.',
            validate: (value) => {
              if (!value) return 'Please upload a file.';
              if (value.type !== MIME_TYPES.csv) return 'File must be CSV.';
              return true;
            },
          }}
          render={({ field, fieldState }) => {
            return (
              <InputWrapper
                label={'Upload CSV'}
                error={fieldState.error?.message}
              >
                <DropzoneInput
                  type={type}
                  setFileErrors={(rowErrors) =>
                    setErrors({
                      rowErrors,
                    })
                  }
                  onChange={field.onChange}
                  fileErrors={errors?.rowErrors ?? []}
                  resetFile={() =>
                    methods.reset({
                      file: undefined,
                      dateFormat: methods.getValues('dateFormat'),
                    })
                  }
                  file={field.value}
                  error={!!fieldState.error}
                  inputHeight={130}
                />
              </InputWrapper>
            );
          }}
        />

        <Banner
          mounted={!!teamScopeBlockReason}
          type="warning"
          title="Team access is updating"
          message={
            teamScopeBlockReason ??
            'Team access is still loading. Wait a moment and retry the import.'
          }
          mt="lg"
          size="sm"
        />

        <Banner
          mounted={!!errors?.fileError}
          type="error"
          title="We couldn't import your file"
          message={
            errors?.fileError ??
            'We have found an issue trying to submit the file. Please reach out to support if this issue persists.'
          }
          mt="lg"
          size="sm"
        />

        <Box
          mt="lg"
          sx={(theme) => ({
            display: 'flex',
            flexWrap: 'nowrap',
            gap: theme.spacing.sm,
          })}
        >
          <Button type="reset" disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            loading={methods.formState.isSubmitting}
            type="submit"
            variant="primary"
            sx={{
              flex: 1,
            }}
          >
            {submitLabel ?? `Import ${toTitleCase(type).toLowerCase()}`}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const DropzoneInput = ({
  type,
  onChange,
  error,
  file,
  resetFile,
  fileErrors,
  setFileErrors,
  inputHeight = 105,
}: {
  type: ImportUniqueRef;
  onChange: (file: FileWithPath) => void;
  error: boolean;
  resetFile: () => void;
  fileErrors: CsvRowError[];
  setFileErrors: (fileErrors: CsvRowError[]) => void;
  file: FileWithPath | undefined;
  inputHeight?: number;
}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [loading, setLoading] = useState(false);
  const [opened, handlers] = useDisclosure(false);

  const iconSize = 30;

  const resetDropZone = () => {
    resetFile();
  };

  return (
    <>
      <Dropzone
        loading={loading}
        onFileDialogOpen={resetDropZone}
        onDrop={(files) => {
          const file = files[0];
          setLoading(true);
          onChange(file);
          setFileErrors([]);
          setLoading(false);
        }}
        onReject={(files) => {
          const file = files[0];
          const message =
            file.errors[0]?.message || 'Please upload a valid CSV file.';

          if (!isFileTypeError(message)) captureSentryError(message);
          showErrorNotification({
            title: 'Invalid file',
            message,
          });
        }}
        maxSize={3 * 1024 ** 2}
        accept={[MIME_TYPES.csv]}
        multiple={false}
        sx={(theme) => {
          const errorBorderColor =
            theme.colors.red[colorScheme === 'dark' ? 4 : 6];
          const defaultBorderColor =
            theme.colors.neutral[colorScheme === 'dark' ? 4 : 2];

          return {
            borderColor: error ? errorBorderColor : defaultBorderColor,
            borderWidth: '2px',
            borderRadius: rem(6),
            borderStyle: 'dashed',
            paddingBlock: theme.spacing.md,
            marginTop: rem(5),
            '&[data-accept]': {
              borderColor: theme.colors[theme.primaryColor][6],
              backgroundColor:
                theme.colors.neutral[colorScheme === 'dark' ? 3 : 0],
            },
            '&[data-reject]': {
              borderColor: errorBorderColor,
            },
          };
        }}
      >
        <Stack
          gap="md"
          align="center"
          justify="center"
          style={{ minHeight: rem(inputHeight), pointerEvents: 'none' }}
        >
          <Dropzone.Accept>
            <Icon
              size={iconSize}
              strokeWidth={1}
              icon="CheckCircleIcon"
              color={
                theme.colors[theme.primaryColor][colorScheme === 'dark' ? 4 : 6]
              }
            />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <Icon
              size={iconSize}
              strokeWidth={1}
              icon="AlertTriangleIcon"
              color={theme.colors.red[colorScheme === 'dark' ? 4 : 6]}
            />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <Icon
              icon={file ? 'FileTextIcon' : 'ImportIcon'}
              size={iconSize}
              strokeWidth={1}
            />
          </Dropzone.Idle>

          {file ? (
            <div>
              <Text
                size="sm"
                ta="center"
                fw={500}
                c={theme.colors[theme.primaryColor][6]}
              >
                {file.name}
                <br />
              </Text>
            </div>
          ) : (
            <div>
              <Text size="sm" c="dimmed" mt={7} ta="center">
                Drag and drop {toTitleCase(type).toLowerCase()} CSV file
                <br />
                or{' '}
                <Text
                  c={theme.colors[theme.primaryColor][6]}
                  component="span"
                  display="inline"
                >
                  select file on disk
                </Text>
              </Text>
            </div>
          )}
        </Stack>
      </Dropzone>

      {!!fileErrors.length && (
        <Card
          mt="sm"
          sx={(theme) => ({
            backgroundColor: theme.colors.red[colorScheme === 'dark' ? 3 : 0],
          })}
        >
          <Text component="p" m={0} fw={500} size="sm" mb="xs">
            Found {fileErrors.length} errors in the file
          </Text>
          <Button
            type="button"
            size="xs"
            onClick={(e) => {
              e.preventDefault();
              handlers.toggle();
            }}
            leftIcon={'FileTextIcon'}
            sx={{
              backgroundColor: 'transparent',
              ':hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            View errors
          </Button>
          <Collapse in={opened}>
            <ScrollArea
              h={rem(inputHeight + 50)}
              mt="xs"
              px={'xs'}
              sx={{
                backgroundColor: '#ffffff80',
              }}
            >
              {fileErrors.map((error, index) => (
                <Text key={index} color="neutral" component="p" size="xs" m={0}>
                  {error.rowNumber
                    ? error.rowNumber === 0
                      ? 'Header: '
                      : `Row ${error.rowNumber}: `
                    : ''}
                  {error.message}
                </Text>
              ))}
            </ScrollArea>
          </Collapse>
        </Card>
      )}
    </>
  );
};
